/* eslint-disable max-lines */
import { useContext, useCallback, useEffect, useRef, useMemo } from "react";
import cx from "classnames";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import {
    AnalyticsContext,
    CartOverlaysContext,
    recordMroCroutonImpression,
    recordMroCroutonTimerComplete,
} from "@swiggy-private/checkout-core";

//types
import { ICroutonList, CroutonIds, GenericCroutonProps, ShowCroutonArgs } from "@swiggy-private/cart-types";
import { IGenericCrouton, MRO_CART_RESPONSE_DRIVEN_CROUTON_TYPES, SUPPORTED_CROUTON_TYPES } from "./types";
import {
    buildCroutonContentFromData,
    buildCroutonRegistration,
    GENERIC_CROUTON_CLASS_NAMES,
    getRemainingDurationInSec,
} from "./helpers";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

//analytics
import { timedFreeCashCroutonImpressionEvent } from "@cart-client/analytics/events/TimedFreeCashCrouton";
import { magicItemsErrorCroutonImpressionEvent } from "@cart-client/analytics/events/CrossSellM2";
import {
    hotelInRoomDeliveryCroutonImpressionEvent,
    impressionMagicItemsTimerCrouton,
} from "@cart-client/analytics/events/genericCrouton";
import { impressionTimerOnCartEvent } from "@cart-client/analytics/events/TimerOnCart";

//utils
import { isEmpty } from "@swiggy-private/js-utils";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";
import styles from "./croutonStyles.scss";

// hooks
import { useDealRushCrouton } from "@cart-client/hooks/useDealRushCrouton";
import { useDeliverySpeedCrouton } from "@cart-client/hooks/useDeliverySpeedCrouton";
import { useIPLSwiggySixesCroutonRegistration } from "@cart-client/hooks/useIPLSwiggySixesCroutonRegistration";
import useMagicItemsCrouton from "../useMagicItemsCrouton/useMagicItemsCrouton";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

//constants
import { CLOSE_ICON_CSS_PROPERTIES, CROUTON_CLOSE_ICON } from "./component/CroutonCloseIcon";

const useGenericCrouton = (props: IGenericCrouton) => {
    const { setCroutonVisibility, canShowTimer, uploadId } = props;
    const { cartState } = useContext(CartContext);
    const { registerCrouton, onCloseCroutonImmediate, enabledCroutonId } = useContext(CartOverlaysContext);
    const { reloadCart } = useContext(UpdateCartContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { reviewedCart: cartResponse } = cartState;
    const croutonList = cartResponse?.data?.croutonList;
    const prevCroutonListRef = useRef<typeof croutonList>();
    const { data } = useContext(CartFlowContext);
    const isMroFlow = isMROFlow(data.cartSource);

    // Owns the IPL Swiggy Sixes crouton end to end (timer updates, payload, priority and the
    // per-tick refresh) and hands back the registration for the crouton list pass below.
    const { getIPLSwiggySixesCroutonRegistration } = useIPLSwiggySixesCroutonRegistration({ setCroutonVisibility });

    const { payloadForCroutonRegister: magicItemsPayloadForCroutonRegister } = useMagicItemsCrouton(croutonList);

    const globalCanShowTimer = useMemo(() => {
        if (!isMroFlow) {
            return canShowTimer;
        }
        return false;
    }, [canShowTimer, isMroFlow]);

    // Use refs to avoid unnecessary re-registrations when context values change
    const impressionEventRef = useRef(impressionEvent);
    impressionEventRef.current = impressionEvent;

    const onCloseCroutonImmediateRef = useRef(onCloseCroutonImmediate);
    onCloseCroutonImmediateRef.current = onCloseCroutonImmediate;

    const reloadCartRef = useRef(reloadCart);
    reloadCartRef.current = reloadCart;

    const dealRushCroutonData = croutonList?.find((crouton) => crouton.type === CroutonIds.DEAL_RUSH)?.data;
    const payloadForCroutonRegister = useDealRushCrouton(dealRushCroutonData);

    const { getDeliverySpeedCroutonRegistration, isDeliverySectionMounted } = useDeliverySpeedCrouton({
        setCroutonVisibility,
        onCloseCroutonImmediate,
    });

    const onCompleteTimer = useCallback(() => {
        if (isMroFlow) {
            recordMroCroutonTimerComplete({ croutonId: "TIMER_RELOAD_CART" });
        }
        reloadCartRef.current();
    }, [isMroFlow]);

    const timerKey = cartState.uniqueIdForCart;

    // Generic impression event handler for all crouton types
    const onCroutonImpressionEvent = useCallback(
        (type: CroutonIds, data: ICroutonList["data"]) => {
            if (isMroFlow) {
                recordMroCroutonImpression({ croutonId: String(type) });
            }
            switch (type) {
                case CroutonIds.TIMED_FREE_CASH_CROUTON:
                    timedFreeCashCroutonImpressionEvent(data?.text || "", impressionEventRef.current, data?.timer, {
                        uploadId,
                        restaurantId: String(cartState.localCart?.restaurantId ?? ""),
                        cartId: String(cartResponse?.data?.cartId ?? ""),
                    });
                    break;
                case CroutonIds.MAGIC_ITEMS:
                    magicItemsErrorCroutonImpressionEvent(
                        impressionEventRef.current,
                        String(cartResponse?.data?.cartId || ""),
                        data?.text || "",
                    );
                    break;

                case CroutonIds.MAGIC_ITEMS_TIMER_CROUTON:
                    impressionMagicItemsTimerCrouton(
                        impressionEventRef.current,
                        String(cartResponse?.data?.cartId || ""),
                        data?.text || "",
                    );
                    break;

                case CroutonIds.HOTEL_IN_ROOM_DELIVERY:
                    hotelInRoomDeliveryCroutonImpressionEvent(
                        impressionEventRef.current,
                        String(cartResponse?.data?.cartId || ""),
                        data?.text || "",
                    );
                    break;
                case CroutonIds.RX_CLOSING_TIMER:
                    // Fire the same impression as the non-MRO BAU restaurant closing timer.
                    impressionTimerOnCartEvent(
                        impressionEventRef.current,
                        !isEmpty(data?.timer) ? JSON.stringify({ timer: data?.timer }) : undefined,
                    );
                    break;
                default:
                    break;
            }
        },
        [cartResponse?.data?.cartId, uploadId, cartState.localCart?.restaurantId, isMroFlow],
    );

    const croutonContent = useCallback(
        (crouton: ICroutonList, index: number) => {
            const { type, data } = crouton;
            const durationInSec = getRemainingDurationInSec(data?.endTimestamp);
            const contentFromData = buildCroutonContentFromData({ data, durationInSec, setCroutonVisibility });

            switch (type) {
                case CroutonIds.IPL_SWIGGY_SIXES:
                    return getIPLSwiggySixesCroutonRegistration(crouton, index);

                case CroutonIds.RX_CLOSING_TIMER: {
                    // drive RX closing timer only in MRO flow via generic crouton and if the duration is available
                    if (!isMroFlow || isEmpty(durationInSec)) {
                        return null;
                    }

                    return buildCroutonRegistration({
                        type,
                        index,
                        content: {
                            ...contentFromData,
                            titleClassName: cx(GENERIC_CROUTON_CLASS_NAMES.titleClassName, styles.rxClosingTimerTitle),
                            timerClassName: cx(styles.rxClosingTimerTicker),
                            onCompleteTimer,
                            cssProperties: data?.backgroundColor ? { background: data.backgroundColor } : undefined,
                            impressionEvent: () => onCroutonImpressionEvent(type, data),
                            timerKey,
                        },
                    });
                }

                case CroutonIds.TIMED_FREE_CASH_CROUTON:
                    return buildCroutonRegistration({
                        type,
                        index,
                        content: {
                            ...contentFromData,
                            ...GENERIC_CROUTON_CLASS_NAMES,
                            onCompleteTimer,
                            cssProperties: data?.backgroundColor ? { background: data.backgroundColor } : undefined,
                            impressionEvent: () => onCroutonImpressionEvent(type, data),
                            timerKey,
                        },
                    });

                case CroutonIds.DEAL_RUSH:
                    if (isEmpty(payloadForCroutonRegister?.title)) {
                        return null;
                    }
                    return buildCroutonRegistration({
                        type,
                        index,
                        content: {
                            ...payloadForCroutonRegister,
                            setCroutonVisibility,
                            wrapperClassName: GENERIC_CROUTON_CLASS_NAMES.wrapperClassName,
                            iconClassName: GENERIC_CROUTON_CLASS_NAMES.iconClassName,
                            buttonClassName: GENERIC_CROUTON_CLASS_NAMES.buttonClassName,
                            timerClassName: GENERIC_CROUTON_CLASS_NAMES.timerClassName,
                        },
                    });

                case CroutonIds.MAGIC_ITEMS:
                    return buildCroutonRegistration({
                        type,
                        index,
                        content: {
                            ...contentFromData,
                            titleClassName: GENERIC_CROUTON_CLASS_NAMES.titleClassName,
                            onCloseCrouton: () => onCloseCroutonImmediateRef.current(),
                            impressionEvent: () => onCroutonImpressionEvent(type, data),
                        },
                    });

                case CroutonIds.MAGIC_ITEMS_TIMER_CROUTON:
                    if (isEmpty(magicItemsPayloadForCroutonRegister?.title)) {
                        return null;
                    }
                    return buildCroutonRegistration({
                        type,
                        index,
                        content: {
                            ...magicItemsPayloadForCroutonRegister,
                            onCompleteTimer,
                            setCroutonVisibility,
                            wrapperClassName: GENERIC_CROUTON_CLASS_NAMES.wrapperClassName,
                            timerClassName: GENERIC_CROUTON_CLASS_NAMES.timerClassName,
                            onCloseCrouton: () => onCloseCroutonImmediateRef.current(),
                            impressionEvent: () => onCroutonImpressionEvent(type, data),
                        },
                    });

                case CroutonIds.DELIVERY_SPEED_TOING:
                    return getDeliverySpeedCroutonRegistration(crouton, index);
                case CroutonIds.TOING_LOOT_DEALS: {
                    const croutonClassNames = data?.showTimer
                        ? GENERIC_CROUTON_CLASS_NAMES
                        : {
                              titleClassName: GENERIC_CROUTON_CLASS_NAMES.titleClassName,
                              iconClassName: GENERIC_CROUTON_CLASS_NAMES.iconClassName,
                          };

                    return {
                        id: type,
                        priority: index + 1,
                        content: {
                            title: data?.text,
                            subText: data?.subText,
                            icon: data?.icon || "",
                            showTimer: data?.showTimer,
                            onCompleteTimer,
                            duration: data?.timer,
                            durationOfCrouton: data?.timeout,
                            setCroutonVisibility,
                            timerTextColor: data?.timerTextColor,
                            textColor: data?.textColor,
                            backgroundColor: data?.backgroundColor,
                            borderRadius: data?.borderRadius,
                            ...croutonClassNames,
                            closeIcon: data?.showTimer ? CROUTON_CLOSE_ICON : null,
                            closeIconCssProperties: data?.showTimer ? CLOSE_ICON_CSS_PROPERTIES : undefined,
                            onCloseCrouton: () => onCloseCroutonImmediateRef.current(),
                            impressionEvent: () => {
                                onCroutonImpressionEvent(type, data);
                            },
                        },
                        forceRefreshContent: true,
                    };
                }
                default:
                    if (!SUPPORTED_CROUTON_TYPES.includes(type)) {
                        return null;
                    }
                    return buildCroutonRegistration({
                        type,
                        index,
                        content: {
                            ...contentFromData,
                            subText: data?.subText,
                            backgroundColor: data?.backgroundColor as GenericCroutonProps["backgroundColor"],
                            borderRadius: data?.borderRadius as GenericCroutonProps["borderRadius"],
                            titleClassName: GENERIC_CROUTON_CLASS_NAMES.titleClassName,
                            iconClassName: GENERIC_CROUTON_CLASS_NAMES.iconClassName,
                            onCloseCrouton: () => onCloseCroutonImmediateRef.current(),
                            impressionEvent: () => onCroutonImpressionEvent(type, data),
                        },
                    });
            }
        },
        [
            onCompleteTimer,
            setCroutonVisibility,
            timerKey,
            payloadForCroutonRegister,
            onCroutonImpressionEvent,
            getDeliverySpeedCroutonRegistration,
            getIPLSwiggySixesCroutonRegistration,
            isMroFlow,
            magicItemsPayloadForCroutonRegister,
        ],
    );

    const isSupportedCroutonType = useCallback(() => {
        if (isEmpty(croutonList)) {
            return false;
        }
        return croutonList?.some((crouton) => SUPPORTED_CROUTON_TYPES.includes(crouton.type));
    }, [croutonList]);

    // MRO: the crouton overlay keeps its own mounted state, so a crouton (typically a running
    // timer) that a newer update-cart response no longer sends - or that the response still sends
    // with an already elapsed endTimestamp - stays on screen until the whole crouton list empties.
    // Cross-check the mounted crouton against every response so it can be dropped immediately.
    const isStaleActiveCrouton = useMemo(() => {
        if (!isMroFlow || isEmpty(enabledCroutonId)) {
            return false;
        }
        if (!MRO_CART_RESPONSE_DRIVEN_CROUTON_TYPES.includes(enabledCroutonId as CroutonIds)) {
            return false;
        }

        const activeCrouton = croutonList?.find((crouton) => crouton.type === enabledCroutonId);
        if (isEmpty(activeCrouton)) {
            return true;
        }

        return (
            Boolean(activeCrouton?.data?.showTimer) &&
            !isEmpty(activeCrouton?.data?.endTimestamp) &&
            getRemainingDurationInSec(activeCrouton?.data?.endTimestamp) <= 0
        );
    }, [croutonList, enabledCroutonId, isMroFlow]);

    const registerGenericCrouton = useCallback(() => {
        if (isEmpty(croutonList)) {
            return;
        }
        croutonList?.forEach((crouton, index) => {
            const registrationArgs = croutonContent(crouton, index) as ShowCroutonArgs;
            if (!isEmpty(registrationArgs)) {
                // Surface endTimestamp on content so the MRO tiebreak can pick the soonest-to-expire
                // crouton. Registrations that track a live expiry (IPL) set it themselves; every
                // other type uses the static value from the crouton list.
                const endTimestamp = registrationArgs.content?.endTimestamp ?? crouton.data?.endTimestamp;
                registerCrouton({
                    ...registrationArgs,
                    content: { ...registrationArgs.content, endTimestamp },
                });
            }
        });
    }, [croutonContent, croutonList, registerCrouton]);

    // Use ref to avoid useEffect running when registerGenericCrouton changes
    const registerGenericCroutonRef = useRef(registerGenericCrouton);
    registerGenericCroutonRef.current = registerGenericCrouton;

    // Track Deal Rush state changes
    const hasDealRushPayload = !isEmpty(payloadForCroutonRegister?.title);
    const dealRushShowTimer = payloadForCroutonRegister?.showTimer;

    useEffect(() => {
        if (globalCanShowTimer) {
            return;
        }

        // Drop a mounted crouton the latest response no longer backs before the list pass below:
        // the mounted-slot defense in useRegisterCrouton would otherwise re-select it. Closing
        // clears enabledCroutonId, which re-runs this effect and registers the surviving croutons.
        if (isStaleActiveCrouton) {
            onCloseCroutonImmediateRef.current();
            return;
        }

        const hadCroutons = !isEmpty(prevCroutonListRef.current);
        const hasCroutons = !isEmpty(croutonList);
        const hasSupportedCroutonType = isSupportedCroutonType();

        if (hadCroutons && (!hasCroutons || !hasSupportedCroutonType)) {
            onCloseCroutonImmediateRef.current();
        } else if (hasSupportedCroutonType) {
            registerGenericCroutonRef.current();
        }

        prevCroutonListRef.current = croutonList;
    }, [
        croutonList,
        isSupportedCroutonType,
        globalCanShowTimer,
        hasDealRushPayload,
        dealRushShowTimer,
        isDeliverySectionMounted,
        isMroFlow,
        isStaleActiveCrouton,
    ]);
};

export default useGenericCrouton;
